import { useMemo } from 'react'

import { FormConfig } from '@workwave-tidal/core/form'

import { useNavigate } from '@/routing'

import { useAlertUserNotAbleToCreateOrders } from '../hooks/useAlertUserNotAbleToCreateOrders'
import { useCollectTargetRange } from '../hooks/useCollectTargetRange'
import { useConfirmFromRecurrenceToSingleOrder } from '../hooks/useConfirmFromRecurrenceToSingleOrder'
import { useOnSubmit } from '../hooks/useOnSubmit/useOnSubmit'

import { form } from '../formState'
import { FormData } from '../types'

import { Footer } from './Footer'
import { FormContent } from './FormContent'
import { Header } from './Header'

interface Props {
  onDiscard: () => void
  updateOrders: (data: uui.domain.ui.forms.BulkOrderUpdate) => Promise<boolean>
  orders: uui.domain.client.rm.ExtendedOrderStep[]
  initialValues: FormData
}

export function Form(props: Props) {
  const { updateOrders, orders, initialValues, onDiscard } = props

  const navigate = useNavigate<uui.routing.Setup>(true)

  const {
    collectTargetRange,
    collectTargetRangePromiseRef,
    allowedTargetRanges,
    setAllowedTargetRanges,
    Modal: CollectRecurrenceTargetRangeModal,
  } = useCollectTargetRange()
  const {
    Modal: ConfirmFromRecurrenceToSingleOrder,
    confirmFromRecurrenceToSingleOrder,
    confirmFromRecurrenceToSingleOrderPromiseRef,
  } = useConfirmFromRecurrenceToSingleOrder()
  const { alertUserUnableToCreateRecurrenceOrder } = useAlertUserNotAbleToCreateOrders()

  // create a submit function linked to
  // - the domain update function
  // - the application editing status
  const { onSubmit } = useOnSubmit({
    updateOrders,
    clearEditingState: onDiscard,
    orders,
    collectTargetRange,
    confirmFromRecurrenceToSingleOrder,
    alertUserUnableToCreateRecurrenceOrder,
    setAllowedTargetRanges,
    navigate,
    initialValues,
  })

  const config = useMemo<FormConfig<FormData>>(
    () => ({
      id: 'bulk-order-form',
      initialValues: initialValues,
    }),
    [initialValues],
  )

  return (
    <>
      <form.Form
        header={<Header ordersCount={orders.length} />}
        footer={<Footer ordersCount={orders.length} handleCancel={onDiscard} />}
        onSubmit={onSubmit}
        config={config}
        rightBorder
        requireTransaction
        stopEditingOnUnmount
        applyPendingFitMapOnMount={true}
        testId="orders-bulk-form"
      >
        <FormContent orders={orders} />
      </form.Form>

      <CollectRecurrenceTargetRangeModal
        allowedTargetRanges={allowedTargetRanges}
        collectTargetRange={targetRange => {
          if (collectTargetRangePromiseRef.current) {
            collectTargetRangePromiseRef.current.resolve(targetRange)
            // collectTargetRangePromiseRef.current = undefined
          }
        }}
        onCancel={() => {
          if (collectTargetRangePromiseRef.current) {
            collectTargetRangePromiseRef.current.reject()
            collectTargetRangePromiseRef.current = undefined
          }
        }}
      />
      <ConfirmFromRecurrenceToSingleOrder
        onConfirm={() => {
          if (confirmFromRecurrenceToSingleOrderPromiseRef.current) {
            confirmFromRecurrenceToSingleOrderPromiseRef.current.resolve()
            confirmFromRecurrenceToSingleOrderPromiseRef.current = undefined
          }
        }}
        onCancel={() => {
          if (confirmFromRecurrenceToSingleOrderPromiseRef.current) {
            confirmFromRecurrenceToSingleOrderPromiseRef.current.reject()
            confirmFromRecurrenceToSingleOrderPromiseRef.current = undefined
          }
        }}
      />
    </>
  )
}
